import React, { useState, useEffect } from 'react';
import { HashRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import './tokens.css';
import './App.css';
import LandingPage from './components/LandingPage';
import PublicLandingPage from './components/PublicLandingPage';
import Login from './components/Login';
import Signup from './components/Signup';
import AdvocateRegistration from './components/AdvocateRegistration';
import AdvocateList from './components/AdvocateList';
import Dashboard from './components/Dashboard';
import RecommendAdvocate from './components/RecommendAdvocate';
import CaseSummarization from './components/CaseSummarization';
import ContractCompliance from './components/ContractCompliance';
import Profile from './components/Profile';
import MessagesPage from './components/Messagespage';
import CaseWorkspace from './components/CaseWorkspace';
import AppSidebar from './components/AppSidebar';
import GuidedTour from './components/GuidedTour';
import OnboardingService from './services/OnboardingService';
import { AuthProvider, useAuth } from './context/AuthContext';
import { supabase } from './supabaseClient';
import lawyerTourSteps from './data/ONBOARDING_LAWYER_TOUR.json';
import clientTourSteps from './data/ONBOARDING_CLIENT_TOUR.json';

function ProtectedApp() {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [selectedAdvocate, setSelectedAdvocate] = useState(null);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [tourActive, setTourActive] = useState(false);
  const [tourStarted, setTourStarted] = useState(false);

  useEffect(() => {
    if (loading) return;

    console.debug('[AuthFlow] state changed', {
      isAuthenticated,
      currentView: view,
      userId: user?.id || null,
    });

  }, [isAuthenticated, loading, view, user]);

  // Tour initialization effect
  useEffect(() => {
    if (loading || !isAuthenticated || !user || tourStarted) return;

    const initializeTour = async () => {
      try {
        const completed = await OnboardingService.hasTourCompleted(user.id);
        if (!completed) {
          // Wait for page to fully render
          const timer = setTimeout(() => {
            setTourActive(true);
            setTourStarted(true);
            // Track event
            OnboardingService.trackEvent('tour_started', {
              user_id: user.id,
              user_role: user.role,
              timestamp: new Date().toISOString(),
            });
          }, 500);
          return () => clearTimeout(timer);
        }
      } catch (error) {
        console.error('Error initializing tour:', error);
      }
    };

    initializeTour();
  }, [isAuthenticated, user, loading, tourStarted]);

  const userRole = user?.role || 'client';
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleTourComplete = async () => {
    try {
      if (user?.id) {
        await OnboardingService.completeTour(user.id, user.role);
      }
      setTourActive(false);
    } catch (error) {
      console.error('Error completing tour:', error);
      setTourActive(false);
    }
  };

  const handleTourSkip = async () => {
    try {
      if (user?.id) {
        await OnboardingService.skipTour(user.id, user.role);
      }
      setTourActive(false);
    } catch (error) {
      console.error('Error skipping tour:', error);
      setTourActive(false);
    }
  };

  const getTourSteps = () => {
    return user?.role === 'lawyer' ? lawyerTourSteps : clientTourSteps;
  };

  const handleNavigate = (dest, extraData) => {
    if (dest === 'messages') {
      setActiveConversationId(extraData || null);
      setView('messages');
    } else if (dest === 'profiles') {
      console.log("[App] Setting selected advocate for Profiles view:", extraData?.name || 'none');
      setSelectedAdvocate(extraData || null);
      setView('profiles');
    } else {
      setSelectedAdvocate(null); // Clear selected lawyer when going elsewhere
      setView(dest);
    }
  };

  const handleOpenWorkspace = async (clientObj) => {
    if (!clientObj?.id || !user?.id) return;
    const { data: rel } = await supabase
      .from('user_relationships')
      .select('id, lawyer_id, client_id, case_type, case_details, specialization, bar_council_id, experience, status')
      .eq('id', clientObj.id)
      .single();
    if (!rel) return;
    const otherId = userRole === 'lawyer' ? rel.client_id : rel.lawyer_id;

    // clientObj already has resolved name + photo from Dashboard's loadRelationships
    // Use those directly, only re-fetch if missing
    let otherProfile = {
      id: otherId,
      full_name: clientObj.name || null,
      photo_url: clientObj.photo_url || null,
      email: clientObj.email || null,
    };

    // If name or photo still missing, fetch from user_profiles
    if (!otherProfile.full_name || !otherProfile.photo_url) {
      if (otherId) {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('id, full_name, photo_url, email')
          .eq('id', otherId)
          .maybeSingle();
        if (prof) {
          otherProfile.full_name = otherProfile.full_name || prof.full_name || null;
          otherProfile.photo_url = otherProfile.photo_url || prof.photo_url || null;
          otherProfile.email = otherProfile.email || prof.email || null;
        }
        // Fallback: advocates table for lawyers
        if (!otherProfile.full_name || !otherProfile.photo_url) {
          const { data: adv } = await supabase
            .from('advocates')
            .select('name, photo_path, email')
            .eq('user_id', otherId)
            .maybeSingle();
          if (adv) {
            otherProfile.full_name = otherProfile.full_name || adv.name || null;
            otherProfile.photo_url = otherProfile.photo_url || adv.photo_path || null;
            otherProfile.email = otherProfile.email || adv.email || null;
          }
        }
      }
    }

    setWorkspaceData({ relationship: rel, otherUser: otherProfile });
    setView('workspace');
  };

  return (
    <div className={`App app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Guided Tour Overlay */}
      {tourActive && (
        <GuidedTour
          steps={getTourSteps()}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
          autoScroll={true}
        />
      )}
      <AppSidebar
        view={view}
        onNavigate={handleNavigate}
        userRole={userRole}
        onLogout={handleLogout}
        user={user}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
      />
      <div className={`app-content app-content-${view}`}>
        {view === 'dashboard' && (
          <Dashboard
            onNavigate={handleNavigate}
            userRole={userRole}
            user={user}
            onOpenWorkspace={handleOpenWorkspace}
            onLogout={handleLogout}
            hideSidebar
          />
        )}
        {view === 'profile' && <Profile userRole={userRole} user={user} onNavigate={handleNavigate} />}
        {view === 'landing' && <LandingPage onNavigate={handleNavigate} />}
        {view === 'messages' && <MessagesPage user={user} userRole={userRole} initialConversationId={activeConversationId} />}
        {view === 'workspace' && workspaceData && (
          <CaseWorkspace
            relationship={workspaceData.relationship}
            otherUser={workspaceData.otherUser}
            user={user}
            userRole={userRole}
            onBack={() => setView('dashboard')}
          />
        )}
        {view === 'recommend' && <RecommendAdvocate onNavigate={handleNavigate} />}
        {view === 'profiles' && (
          <AdvocateList 
            onNavigate={handleNavigate} 
            user={user} 
            initialSelectedAdvocate={selectedAdvocate} 
          />
        )}
        {userRole === 'lawyer' && view === 'register' && <AdvocateRegistration onNavigate={handleNavigate} user={user} />}
        {view === 'summarize' && <CaseSummarization onNavigate={handleNavigate} />}
        {view === 'compliance' && <ContractCompliance onNavigate={handleNavigate} />}
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <ProtectedApp />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicLandingPage />} />
      <Route path="/clients" element={<PublicLandingPage audience="clients" />} />
      <Route path="/advocates" element={<PublicLandingPage audience="advocates" />} />
      <Route
        path="/login"
        element={(
          <Login
            onLogin={() => navigate('/')}
            onSignupClick={() => navigate('/signup')}
          />
        )}
      />
      <Route
        path="/signup"
        element={(
          <Signup
            onSignup={() => navigate('/login')}
            onLoginClick={() => navigate('/login')}
          />
        )}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </HashRouter>
  );
}

export default App;
